/**
 * Detects image formats that browsers generally cannot decode in a plain <img>:
 *   - HEIC / HEIF
 *   - Camera RAW formats (ARW, CR2, CR3, CRW, DNG, NEF, NRW, ORF, RAF, RW2,
 *     RWL, SRW, PEF, DCR, K25, KDC, MRW, ERF, SR2, SRF, X3F, 3FR, IIQ, MEF,
 *     MOS, FFF, DCS)
 *   - TIFF (commonly a RAW container)
 *   - PSD / PSB / PDD / PSDT (Photoshop)
 *   - AI (Adobe Illustrator)
 *   - EPS / PostScript
 *
 * Such files must be shown from a pre-rendered raster thumbnail in the UI
 * (e.g. crop preview) instead of streaming the original source URL.
 *
 * Note: GIF and SVG are intentionally NOT in this list — every modern
 * browser renders them natively, so they can be streamed as-is.
 *
 * The check is intentionally tolerant: it looks at the MIME type first and
 * falls back to the file extension when the MIME type is missing, generic
 * (e.g. application/octet-stream) or not enough to decide.
 *
 * @param {string|undefined} mimeType
 * @param {string|undefined} [fileExtension]
 * @returns {boolean}
 */
const NON_BROWSER_MIME_TYPES = new Set([
  // HEIC / HEIF
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
  // TIFF
  'image/tiff',
  // Camera RAW
  'image/x-adobe-dng',
  'image/x-canon-cr2',
  'image/x-canon-cr3',
  'image/x-canon-crw',
  'image/x-dcraw',
  'image/x-epson-erf',
  'image/x-fuji-raf',
  'image/x-hasselblad-3fr',
  'image/x-hasselblad-fff',
  'image/x-kodak-dcr',
  'image/x-kodak-dcs',
  'image/x-kodak-k25',
  'image/x-kodak-kdc',
  'image/x-leaf-mos',
  'image/x-mamiya-mef',
  'image/x-minolta-mrw',
  'image/x-nikon-nef',
  'image/x-nikon-nrw',
  'image/x-olympus-orf',
  'image/x-panasonic-raw',
  'image/x-panasonic-rw2',
  'image/x-pentax-pef',
  'image/x-phaseone-iqq',
  'image/x-phaseone-iiq',
  'image/x-samsung-srw',
  'image/x-sigma-x3f',
  'image/x-sony-arw',
  'image/x-sony-sr2',
  'image/x-sony-srf',
  'image/x-leica-rwl',
  // Photoshop
  'image/vnd.adobe.photoshop',
  'application/x-photoshop',
  'application/photoshop',
  'image/photoshop',
  'image/psd',
  'application/psd',
  // Illustrator / PostScript / EPS
  'application/illustrator',
  'application/postscript',
  'application/eps',
  'application/x-eps',
  'image/eps',
  'image/x-eps',
]);

const NON_BROWSER_EXTENSIONS = new Set([
  // HEIC/HEIF
  'heic', 'heif', 'heics', 'heifs',
  // TIFF
  'tif', 'tiff',
  // Camera RAW
  '3fr', 'arw', 'cr2', 'cr3', 'crw', 'dcr', 'dcs', 'dng', 'erf', 'fff',
  'iiq', 'k25', 'kdc', 'mef', 'mos', 'mrw', 'nef', 'nrw', 'orf', 'pef',
  'raf', 'raw', 'rw2', 'rwl', 'sr2', 'srf', 'srw', 'x3f',
  // Photoshop
  'psd', 'psb', 'pdd', 'psdt',
  // Illustrator / EPS
  'ai', 'eps',
]);

export default function isNonBrowserRenderableImageMime(mimeType, fileExtension) {
  if (mimeType && typeof mimeType === 'string') {
    const m = mimeType.toLowerCase().trim();
    if (NON_BROWSER_MIME_TYPES.has(m)) return true;
    // Tolerant fallback for vendor-prefixed MIME variants like
    // "image/x-sony-arw; charset=binary" or unknown "x-<brand>-<ext>".
    if (/^image\/x-[a-z-]+$/.test(m)) return true;
    if (/\b(heic|heif)\b/.test(m)) return true;
  }

  if (fileExtension && typeof fileExtension === 'string') {
    const ext = fileExtension.toLowerCase().replace(/^\./, '').trim();
    if (ext && NON_BROWSER_EXTENSIONS.has(ext)) return true;
  }

  return false;
}
